import type { EntryCollection, EntrySkeletonType } from 'contentful'

export const usePreviewBlogArticleFilter = () => {
  const { getPanelInfo } = useScheduledActions()

  const filterContentNodes = (contentArray, tag: string) => {
    if (!Array.isArray(contentArray)) return contentArray
    return contentArray.filter((node) => hasPersonalizationTag(node?.data?.target, tag))
  }

  const filterFieldByTag = (value, tag: string): boolean => {
    if (typeof value !== 'object' || value === null) return true

    const personalizationTags = value.fields?.personalizationTags || []
    const contentTags = value.fields?.content?.personalizationTags || []
    const allTags = [...personalizationTags, ...contentTags]

    // Include if no tags
    if (!Array.isArray(allTags) || allTags.length === 0) return true

    // Exclude if has conflicting (non-matching) tags
    if (allTags.some((t: string) => t !== tag)) return false

    // Include only if has matching tag
    return allTags.includes(tag)
  }

  const filterRichTextContentByTag = (value, tag: string) => {
    if (!isRichTextContent(value)) return value

    return {
      ...value,
      content: filterContentNodes(value.content, tag),
    }
  }

  const filterByPersonalizationTag = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => {
    const tag = previewConfig.previewPersonalizationTag

    return {
      ...entries,
      items: entries?.items?.map((item) => {
        const fields = { ...item?.fields }
        const newFields = {}

        Object.entries(fields).forEach(([key, value]: [string, any]) => {
          // Preserve non-object values as-is
          if (typeof value !== 'object' || value === null) {
            newFields[key] = value
            return
          }

          // Filter array fields
          if (Array.isArray(value)) {
            newFields[key] = value.filter((item) => filterFieldByTag(item, tag))
            return
          }

          // Filter rich text content nodes
          if (isRichTextContent(value)) {
            newFields[key] = filterRichTextContentByTag(value, tag)
            return
          }

          // Filter by personalization tags
          if (filterFieldByTag(value, tag)) {
            newFields[key] = value
          }
        })

        return {
          ...item,
          fields: newFields,
        }
      }),
    }
  }

  const getAllPanels = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ): PanelInfo[] => {
    const { items } = entries
    if (!items?.length) return []

    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return []

    const panels: PanelInfo[] = []

    Object.entries(firstPage).forEach(([key, value]) => {
      // Extract from content field
      if (isRichTextContent(value)) {
        extractPanelsFromRichTextContent(value).forEach((panel) => panels.push(getPanelInfo(panel)))
        return
      }

      // Extract from array fields
      if (Array.isArray(value)) {
        extractPanelsFromArray(value).forEach((panel) => panels.push(getPanelInfo(panel)))
        return
      }

      // Extract from regular panel fields
      const panelId = extractPanelId(value)
      if (panelId) {
        panels.push(getPanelInfo(value as LocalePageEntry))
      }
    })

    // add parent page info to panels
    panels.push(getPanelInfo(entries.items[0], { isParentPage: true }))

    return panels
  }

  const removePanelsForDisplayInfo = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    panelDisplayInfo: Record<string, { show: boolean | string }>,
    previewConfig: PreviewConfig,
    options?: { panels?: PanelInfo[]; scheduledActions?: Record<string, any> },
  ) => {
    const { items } = entries
    if (!items?.length) return entries

    const firstPage = getFirstPageFields(entries)
    if (!Object.keys(firstPage).length) return entries

    if (shouldHidePreviewPayload(previewConfig, panelDisplayInfo, options)) {
      entries.items = []
      return entries
    }

    Object.entries(firstPage).forEach(([key, value]) => {
      // Filter content field
      if (isRichTextContent(value)) {
        ;(value as any).content = filterContentByDisplayInfo((value as any).content, panelDisplayInfo)
        return
      }

      // Filter array fields
      if (Array.isArray(value)) {
        firstPage[key] = filterArrayByDisplayInfo(value, panelDisplayInfo)
        return
      }

      // Remove single panel fields if show is false
      const panelId = extractPanelId(value)
      if (panelId && panelDisplayInfo[panelId]?.show === false) {
        delete firstPage[key]
      }
    })

    entries.items[0].fields = firstPage
    return entries
  }

  const filterByMarket = (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterEntriesByMarket(entries, previewConfig.previewMarket as string)

  const filterByPreviewDate = async (
    entries: EntryCollection<EntrySkeletonType, undefined, string>,
    previewConfig: PreviewConfig,
  ) => filterByPreviewDateOrchestration(entries, previewConfig, getAllPanels, removePanelsForDisplayInfo)

  return {
    filterByPersonalizationTag,
    filterByMarket,
    filterByPreviewDate,
    getAllPanels,
  }
}
